/* Momentum's colours, and the handful of style objects every panel shares.
 *
 * Split out of MomentumHub.jsx when that file reached 3,700 lines and each panel
 * became its own file. Nothing here renders — the shared components are in
 * ui.jsx, and anything that knows what a page or a module IS belongs in that
 * panel's file.
 */

/* Momentum's body is a CANVAS surface — it follows the Light / Dark panel
   toggle like the chat and the side panels. Only the header bar below is
   chrome. `B` keeps its original key names so the ~200 call sites don't move;
   every value is now a canvas token.

   Two things a var() cannot do, hence the extra keys:
     • alpha suffixes — `${B.teal}1f` is invalid against `var(--accent)`, so the
       tinted variants are their own tokens (…Quiet / …Line).
     • a solid accent fill — `--accent` is #0AEFD3 on dark, where white text is
       ~1.4:1. Solid buttons use `--btn-bg` / `--btn-text` instead.                */
export const B = {
  bg: "var(--bg-primary)",
  surface: "var(--bg-surface)",
  surface2: "var(--bg-sunken)",
  border: "var(--border-subtle)",
  border2: "var(--border-strong)",
  ghost: "var(--border-strong)",
  text: "var(--text-primary)",
  mid: "var(--text-secondary)",
  dim: "var(--text-muted)",
  // Uppercase micro-labels (field labels, breadcrumbs, table heads). Was the
  // dimmest step in the old ramp, which put it at ~2.3:1 once the canvas can be
  // light — these carry meaning, so they sit on the readable step now.
  label: "var(--text-secondary)",
  faint: "var(--text-muted)",
  // The old `teal` / `tealLight` pair — a deep fill plus a bright-on-dark text
  // colour — collapses to one token, because the per-scope swap it was hand-
  // rolling is exactly what `--accent` does (#0AEFD3 dark / #007D6E light).
  teal: "var(--accent)",
  tealQuiet: "var(--accent-quiet)",
  tealLine: "var(--accent-line)",
  // Amber as *text* has to darken on a light canvas (#F59E0B is ~2.1:1 on
  // white); as a fill it stays amber. Hence the split.
  amber: "var(--warn-text)",
  amberFill: "var(--status-draft)",
  amberQuiet: "var(--warn-bg)",
  amberLine: "var(--warn-line)",
  salmon: "var(--danger-text)",
  salmonQuiet: "var(--danger-quiet)",
  salmonLine: "var(--danger-line)",
};

// Ink for text sitting on a solid amber fill — legible over both scopes'
// `--status-draft`, which neither `--btn-text` nor `--text-primary` manages.
export const ON_AMBER = "#1A1206";

/* The dot every list reads its state from, so one record can't be green in one
   place and grey in another. */
export const STATUS_DOT = { done: "teal", draft: "amberFill", empty: "ghost" };
export const STATUS_LABELS = { locked: "Locked", draft: "Draft", retired: "Retired" };

export const blockIcon = { background: "none", border: "none", color: B.faint, padding: 4, display: "flex", alignItems: "center", cursor: "pointer" };
export const quickBtn = { padding: "6px 12px", borderRadius: 16, fontSize: 12, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${B.tealLine}`, background: B.tealQuiet, color: B.teal };
export const pTitle = { fontSize: 22, fontWeight: 700, margin: "0 0 6px", color: B.text };
export const pDesc = { fontSize: 13.5, color: B.mid, lineHeight: 1.6, margin: "0 0 24px", maxWidth: 640 };
/* A heading with NO description under it. `pTitle` only carries 6px, because for
   two years something always followed it and `pDesc` held the other 24 — so when
   the four Momentum headings lost their paragraphs on 2026-09-15 (rule 9, and
   her "that is a lot of words"), every one of them ended up sitting on top of
   its own toolbar. Her report: "when we took out all that text below Sites the
   spacing became cramped up top."

   Its own token rather than four margins typed into four files, for the usual
   reason: four numbers drift and one does not. */
export const pTitleAlone = { ...pTitle, margin: "0 0 24px" };
export const input = { width: "100%", background: "var(--input-bg)", border: `1px solid ${B.border2}`, borderRadius: 9, padding: "11px 13px", color: B.text, fontSize: 13.5, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
export const btnPrimary = { padding: "11px 20px", borderRadius: 10, background: "var(--btn-bg)", border: "1px solid var(--btn-bg)", color: "var(--btn-text)", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
export const btnGhost = { padding: "11px 20px", borderRadius: 10, background: "transparent", border: `1px solid ${B.border2}`, color: B.mid, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
export const soonCard = { border: `1px solid ${B.border}`, borderRadius: 12, padding: "26px 24px", background: B.surface, display: "flex", gap: 16, alignItems: "flex-start" };
export const soonBadge = { display: "inline-block", marginTop: 12, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: B.amber, border: `1px solid ${B.amberLine}`, background: B.amberQuiet, borderRadius: 20, padding: "3px 11px" };
export const pillBase = { flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 10, padding: "1px 6px" };
export const nb = { color: B.mid, fontStyle: "normal", fontWeight: 700 };
